import { useCallback, useState } from 'react';
import { useWorkspaceNavigate } from '../../workspace-path';
import {
  AGENT_FLAG,
  addWireDays,
  dashboardViewSchema,
  toWireDate,
  wireMonday,
  type DashboardView,
} from '@forecastify/shared';
import { useT } from '../../i18n';
import { useLiveInvalidation } from '../../live/use-live';
import { HOME_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { ScreenState, failureOf, type ScreenFailure } from '../screen-state';
import { useFirstLoad } from '../use-first-load';
import { DashboardScreen } from './dashboard-screen';
import { useSidebarContext } from '../sidebar/use-sidebar';
import { greetingKey } from '../agent/greeting';
import { handOffToAgent } from '../agent/agent-handoff';
import { useMyWeek } from './use-my-week';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Kezdőlap's fetching. The screen renders props.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **16 Oct 2026 · the period is always THIS week.** The reworked home has no chart and no
 * period control — Időrögzítés owns the week and month view now — so the range sent is the current
 * week's, which is what the agent's home tool asks for too.
 */
/** What the screen draws before anything has arrived. See `view`'s note. */
const EMPTY_VIEW: DashboardView = {
  weekHours: '0:00',
  openTasks: 0,
  gapTaskCount: 0,
  activeProjects: 0,
  days: [],
  from: '',
  to: '',
  recentLogs: [],
  gapTasks: [],
  todayPlanned: null,
  weekFrame: null,
  weekLogged: null,
  pendingRequests: 0,
  upcomingLeaveDays: 0,
  quotas: [],
  overtimeSubmitted: 0,
  overtimeApprovedHours: '0.00',
  overtimeAccountedHours: '0.00',
  todayHours: '0.00',
  weekPlanned: null,
  monthLabel: new Date().toISOString().slice(0, 7),
  monthHours: '0.00',
  monthPlannedToDate: null,
  missingDays: 0,
  weekHoursLogged: '0.00',
  weekLeft: null,
  weekMissingDays: 0,
  pendingRows: [],
  upcomingRows: [],
  overtimeYearHours: '0.00',
  overtimeYearAccounted: '0.00',
  overtimePendingHours: '0.00',
  overtimePendingCount: 0,
  overtimeMonthHours: '0.00',
  overtimeMonthCount: 0,
  overtimeWaitingOnMe: 0,
  overtimeRows: [],
  overtimePendingRows: [],
  overtimeApprovedRows: [],
  taskChips: [],
  myTasks: [],
  overdueTasks: 0,
  dueTodayTasks: 0,
};

export function DashboardRoute() {
  const session = useSession();
  const t = useT();
  /* ⚠️ R-38 · `go`, not `navigate`: it takes a WORKSPACE-RELATIVE path and supplies the first
     segment. The name is what lets `eslint.config.mjs` ban a bare `go('/tasks')`,
     which under R-38 names a workspace rather than a screen. */
  const go = useWorkspaceNavigate();
  /* ⚠️ `=== true`, as the Agent's own MCP button reads its flag: not answered yet is not yet. */
  const sidebarHandle = useSidebarContext();
  const agentOn = sidebarHandle?.view.features[AGENT_FLAG] === true;
  /* 26 Oct 2026 · somebody who does not log time has no *Időlogok* card. */
  const logsTime = sidebarHandle?.view.entitlements.logsTime !== false;

  const today = toWireDate(new Date());
  const monday = wireMonday(today) ?? today;
  const sunday = addWireDays(monday, 6) ?? monday;

  /**
   * ⚠⚠️ **AN EMPTY VIEW RATHER THAN `null`, SO THE SCREEN ALWAYS RENDERS ITS MARKER.** The
   * reachability suite asserts that every address renders `data-screen="home"` — and a route that
   * withholds its screen until a fetch resolves renders nothing at all on the first frame.
   */
  const [view, setView] = useState<DashboardView>(EMPTY_VIEW);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const { week } = useMyWeek(today);

  /**
   * ⚠️ **The greeting is the Agent's own**, picked once per visit — `agent-screen.tsx` argues why
   * the pick is state rather than a call in the body.
   */
  const [pick] = useState(() => Math.random() * 1000);
  const greeting =
    session.givenName === null
      ? t('agent.greeting')
      : t(greetingKey(new Date().getHours(), pick), { name: session.givenName });

  const reload = useCallback(async (): Promise<void> => {
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      return;
    }
    const response = await session
      .request(`/workspace/dashboard?from=${monday}&to=${sunday}`)
      .catch(() => null);

    /* ⚠️ Three outcomes and none of them a silent `return` — `hard-won.md` C6-9 and C6-11 are both
     * spinners that never stopped. */
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    const parsed = dashboardViewSchema.safeParse(await response.json().catch(() => null));
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    setFailure(null);
    setView(parsed.data);
  }, [session, monday, sunday]);

  /* First load draws skeleton rows — `use-first-load.ts`. */
  const loading = useFirstLoad(reload);

  /* ⚠️ C21 · the same constant `routes.tsx` declares for this screen. R-19. */
  useLiveInvalidation(HOME_TOPICS, reload);

  return (
    <ScreenState failure={failure} onRetry={() => void reload()}>
      <DashboardScreen
        loading={loading}
        view={view}
        week={week}
        agentOn={agentOn}
        logsTime={logsTime}
        greeting={greeting}
        handlers={{
          onGoTime: () => {
            go('/time');
          },
          onOpenRequest: (id) => {
            go(`/requests/${id}`);
          },
          /* ⚠️ A claim has no page of its own (`routes.tsx`), so it opens over Kérelmeim's
             Túlóra tab — the same drawer a row there opens. */
          onOpenClaim: (id) => {
            go(`/requests?tab=overtime&open=${id}`);
          },
          onGoLeave: () => {
            go('/requests');
          },
          onGoOvertime: () => {
            go('/requests?tab=overtime');
          },
          onOpenLineItem: (contract, lineItem) => {
            go(`/contracts/${contract}/${lineItem}`);
          },
          onAsk: (text, files) => {
            handOffToAgent({ text, files });
            go('/agent');
          },
        }}
      />
    </ScreenState>
  );
}
